import useSWR from "swr"
import { useEffect, useState } from "react"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"
import url from "url"
import dynamic from "next/dynamic"
import * as Sentry from "@sentry/nextjs"
import {
  GET_INFOGRAPHICS,
  GetInfographics,
  GetInfographicsVariables,
} from "@graphql/homepage/infographics"
import fetcher from "lib/fetcher"
import FreeTrial from "@components/Sections/FreeTrial"
import Categories from "@components/Sections/Categories"
import Hero from "@components/Sections/Business/Hero"
import CorporateSolutionServices from "@components/Sections/Business/CorporateSolutionServices"
import BusinessGoals from "@components/Sections/Business/BusinessGoals"
import ExclusiveExperience from "@components/Sections/Business/ExclusiveExperience"
import BusinessForm from "@components/Sections/Business/BusinessForm"
import BusinessFaqs from "@components/Sections/Business/Faqs"
import BubbleChat from "@components/shared-components/BubbleChat"
import { initializeApollo } from "lib/apollo"

const WhatOurClientSays = dynamic(() => import("@components/Sections/WhatOurClientSays"), {
  ssr: false,
})
const RegisterYourCompany = dynamic(
  () => import("@components/Sections/Business/RegisterYourCompany"),
  { ssr: false },
)
// Reserves the section's rendered height (see its min-h) so mounting after hydration doesn't
// push the next section out of view.
const TrustedCompanies = dynamic(() => import("@components/Sections/Business/TrustedCompanies"), {
  ssr: false,
  loading: () => <div className="min-h-[385px] md:min-h-[425px] lg:min-h-[553px]" />,
})
const SimpleSteps = dynamic(() => import("@components/Sections/Business/SimpleSteps"), {
  ssr: false,
})

export const getServerSideProps = async (ctx) => {
  let activeLocale = ctx.locale
  const language = ctx.query.lang

  const cookieHeader = ctx.req.headers.cookie || ""
  const cookies = Object.fromEntries(cookieHeader.split("; ").map((c) => c.split("=")))

  if (language) {
    activeLocale = language
  }

  const dataLanguage = await import(
    `../public/locales/${activeLocale.toUpperCase()}/translations.json`
  )

  const descriptionName = dataLanguage.description_seo_corporate_solution
  const metaTitle = dataLanguage.title_seo_corporate_solution

  // Public catalogue data: the public role (server-only token), never the admin secret.
  const apolloClient = initializeApollo()

  // Seeds the Hero stats grid; client-fetching it cost a 0.19 layout shift. Decorative, so a
  // failure degrades to client SWR instead of 500-ing the page.
  let infographics: GetInfographics | null = null
  try {
    const { data, errors } = await apolloClient.query<GetInfographics, GetInfographicsVariables>({
      query: GET_INFOGRAPHICS,
      variables: { type: "business" },
    })
    // errorPolicy "all" resolves a Hasura fault instead of rejecting, so the catch never sees it.
    if (errors?.length) {
      Sentry.captureMessage(`infographics query: ${errors.map((e) => e.message).join("; ")}`, {
        level: "warning",
      })
    }
    // Stays null unless the list arrived: JSON.stringify drops an undefined prop, and a partial
    // result would pin SWR to an empty grid since revalidateIfStale is off.
    infographics = data?.infographics ? data : null
  } catch (error) {
    Sentry.captureException(error)
  }

  return {
    props: {
      descriptionName,
      metaTitle,
      infographics,
      bubbleChat: !!cookies?.bubbleChat,
    },
  }
}

export default function Business({ metaTitle, descriptionName, bubbleChat, infographics }) {
  const router = useRouter()
  // fallbackData alone does not suppress the fetch — revalidateIfStale defaults true, and the
  // refetch reads via /api/infographics on the public-role token, unordered, while Hero maps
  // LIST_ICONS by array position. Undefined fallbackData still revalidates, so failures recover.
  const { data: dataInfographics } = useSWR("/api/infographics?name=business", fetcher, {
    fallbackData: infographics ?? undefined,
    revalidateIfStale: false,
  })
  const { data: dataInfographicsHome } = useSWR("/api/infographics?name=homepage", fetcher)
  const { data: dataCompanies } = useSWR("/api/client/list", fetcher)
  const { pathname } = url.parse(router.asPath)
  const totalCompanies = dataInfographicsHome?.infographics?.find(
    (it) => it.title === "Corporate Clients",
  )
  const [close, setClose] = useState(bubbleChat)

  useEffect(() => {
    const hash = router.asPath.split("#")[1]
    if (hash === "form_leads_section") {
      let scrollAttempt = 0
      const maxAttempts = 10

      const tryScroll = () => {
        const el = document.getElementById("form_leads_section")
        if (!el || scrollAttempt > maxAttempts) return

        const y = el.getBoundingClientRect().top + window.pageYOffset - 80
        window.scrollTo({ top: y, behavior: "smooth" })

        const currentScroll = window.pageYOffset
        setTimeout(() => {
          const newScroll = window.pageYOffset
          if (Math.abs(newScroll - currentScroll) > 10 && scrollAttempt < maxAttempts) {
            scrollAttempt += 1
            tryScroll()
          }
        }, 400)
      }

      setTimeout(() => {
        if (typeof requestIdleCallback === "function") {
          requestIdleCallback(tryScroll)
        } else {
          tryScroll()
        }
      }, 300) // initial delay before start polling scroll
    }
  }, [router.asPath])

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={descriptionName}
        canonical={`${process.env.NEXT_PUBLIC_URL}${pathname}`}
        openGraph={{
          type: "website",
          locale: "en_ID",
          url: `${process.env.NEXT_PUBLIC_URL}${pathname}`,
          title: `${metaTitle}`,
          site_name: `${metaTitle}`,
          description: descriptionName,
          images: [
            {
              url: "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1721139866/open-graph/main-category/corporate-solution.jpg",
              alt: `${metaTitle}`,
              width: 800,
              height: 600,
            },
          ],
        }}
      />
      <main className="relative">
        <Hero listData={dataInfographics?.infographics} />
        <TrustedCompanies totalCompanies={totalCompanies?.description} companies={dataCompanies} />
        <CorporateSolutionServices />
        <BusinessGoals />
        <RegisterYourCompany />
        <FreeTrial />
        <SimpleSteps />
        <ExclusiveExperience />
        <WhatOurClientSays />
        <Categories />
        <BusinessForm />
        <BusinessFaqs />
        {!close && <BubbleChat setClose={setClose} />}
      </main>
    </>
  )
}
